<template>
  <div>
    <h2 class="heading-tertiary">{{ $t('parameters.users.account') }}</h2>
    <FilterList
      indicator='parameters.users.filter'
      :regEx="{
        or:{
          firstname: /^[a-zA-Z\-]+$/,
          name: /^[a-zA-Z\-]+$/,
          email: /^[a-zA-Z\-0-9.]+@[a-zA-Z\-0-9.]+/
        }
      }"
      @update:filter="resetUsers($event)"
    />

    <div class="parameters__table">
      <el-table
        ref="userTable"
        :data="usersList"
        :row-key="usersList._id"
        :default-sort = "{prop: 'name', order: 'ascending'}"
        border
        stripe
        header-row-class-name="table__header"
        row-class-name="table__row"
        class="table"
        v-infinite-scroll="getUsers"
        infinite-scroll-disabled="disableInfiniteScroll"
        infinite-scroll-distance=100
        v-loading="loadingUsers"
      >
        <el-table-column
          prop="name"
          :label="$t('parameters.users.list.name')"
          sortable
          min-width="15"
        >
        </el-table-column>
        <el-table-column
          prop="firstname"
          :label="$t('parameters.users.list.firstname')"
          sortable
          min-width="15"
        >
        </el-table-column>
        <el-table-column
          prop="email"
          :label="$t('parameters.users.list.email')"
          sortable
          min-width="30"
        >
        </el-table-column>
        <el-table-column
          prop="status"
          :label="$t('parameters.users.list.status')"
          sortable
          :formatter="statusMessage"
          min-width="15"
        >
        </el-table-column>
        <el-table-column
          :label="$t('parameters.users.list.action')"
          min-width="25"
        >
          <template slot-scope="scope">
            <svg v-show="scope.row.status == 3" class="table__icon table__icon--medium table__icon--red">
              <use href="../assets/sprite.svg#icon-power-off"></use>
            </svg>
            <svg v-if="$can('update', 'users')" class="table__icon table__icon--medium" @click="showEditUser(true); currentRow = scope.row">
              <use href="../assets/sprite.svg#icon-pencil"></use>
            </svg>
            <svg v-if="$can('delete', 'users')" class="table__icon table__icon--medium" @click="deleteUser(scope.row)">
              <use href="../assets/sprite.svg#icon-cancel"></use>
            </svg>
            <svg v-if="$can('deactivate', 'users')" v-show="scope.row.status > 0" class="table__icon table__icon--medium" @click="setActive(scope.row, 0)">
              <use href="../assets/sprite.svg#icon-user-minus"></use>
            </svg>
            <svg v-if="$can('activate', 'users')" v-show="scope.row.status == 0" class="table__icon table__icon--medium" @click="setActive(scope.row, 1)">
              <use href="../assets/sprite.svg#icon-user-check"></use>
            </svg>
          </template>
        </el-table-column>
      </el-table>
      <transition name="popup">
        <UserAdd v-if='getShowAddUser' @popup:close='resetUsers'></UserAdd>
        <UserEdit v-if='getShowEditUser' :user="currentRow" @popup:close='resetUsers'></UserEdit>
      </transition>
    </div>
  </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex'
import { HTTP_CLIENT } from '@/axios-wrapper'
import { toastMixin } from '@/components/Mixins/Toast'
import { dialogboxMixin } from '@/components/Mixins/Dialogbox'
import UserEdit from '@/views/popup/UserEdit.vue'
import UserAdd from '@/views/popup/UserAdd.vue'
import FilterList from '@/components/FilterList'

const { mapGetters, mapMutations } = createNamespacedHelpers('user')

export default {
  data: function () {
    return {
      usersList: [],
      searchInput: '',
      timeout: null,
      currentRow: '',
      currentPage: 1,
      noMoreUsers: false,
      loadingUsers: false,
      filterUsers: '',
      dialogBoxDelete: {
        title: this.$t('dialog.user.delete.title'),
        content: this.$t('dialog.user.delete.content'),
        cancelAction: this.$t('dialog.user.delete.cancelAction'),
        confirmAction: this.$t('dialog.user.delete.confirmAction')
      },
      dialogBoxInactive: {
        title: this.$t('dialog.user.inactive.title'),
        content: this.$t('dialog.user.inactive.content'),
        cancelAction: this.$t('dialog.user.inactive.cancelAction'),
        confirmAction: this.$t('dialog.user.inactive.confirmAction')
      },
      dialogBoxActive: {
        title: this.$t('dialog.user.active.title'),
        content: this.$t('dialog.user.active.content'),
        cancelAction: this.$t('dialog.user.active.cancelAction'),
        confirmAction: this.$t('dialog.user.active.confirmAction')
      }
    }
  },
  components: {
    UserEdit,
    UserAdd,
    FilterList
  },
  computed: {
    ...mapGetters(['getShowEditUser', 'getShowAddUser']),
    disableInfiniteScroll () {
      return this.noMoreUsers || this.loadingUsers
    }
  },
  mixins: [toastMixin, dialogboxMixin],
  methods: {
    ...mapMutations({
      showEditUser: 'SHOW_EDIT_USER_POPUP' }),
    resetUsers (filter) {
      this.filterUsers = filter !== undefined ? filter : this.filterUsers
      this.currentPage = 1
      this.noMoreUsers = false
      this.usersList = []
      this.getUsers()
    },
    async getUsers () {
      try {
        this.loadingUsers = true
        let { data } = await HTTP_CLIENT.get(`user?page=${this.currentPage}&limit=12${this.filterUsers ? '&' + this.filterUsers : ''}`)
        if (data.length) {
          this.usersList.push(...data)
          this.noMoreUsers = false
          this.currentPage++
        } else {
          this.noMoreUsers = true
        }
        this.loadingUsers = false
      } catch (error) { console.log(error) }
    },
    async deleteUser (user) {
      try {
        const ret = await this.msgbox(this.dialogBoxDelete)
        if (ret === 'confirm') {
          await HTTP_CLIENT.delete(`user/${user._id}`)
          this.resetUsers()
          this.sendSuccessToast(this.$t('feedback-ok.user.delete', { firstname: user.firstname, name: user.name }))
        }
      } catch (error) { console.log(error) }
    },
    async setActive (user, active) {
      try {
        let ret
        if (!active) ret = await this.msgbox(this.dialogBoxInactive)
        else ret = await this.msgbox(this.dialogBoxActive)

        if (ret === 'confirm') {
          await HTTP_CLIENT.patch(`user/${user._id}`, {
            active,
            status: active
          })

          if (active) {
            this.sendSuccessToast(this.$t('feedback-ok.user.active', { firstname: user.firstname, name: user.name }))
          } else {
            this.sendSuccessToast(this.$t('feedback-ok.user.inactive', { firstname: user.firstname, name: user.name }))
          }
          this.resetUsers()
        }
      } catch (err) { console.log('err', err) }
    },
    statusMessage: function (row, column, value, index) {
      switch (value) {
        case 0 : return 'Inactif'
        case 1 : return 'Actif'
        case 2 : return 'Déconnecté'
        case 3 : return 'Connecté'
        default : return ''
      }
    }
  },
  async created () {
    // Ajout des icone de la vue ds la toolbar
    let toolbarIconsID = await this.$store.dispatch('subheader/ADD_ICON_TOOLBAR', [{
      svg: 'icon-user-plus',
      fn: () => { return this.$store.commit('user/SHOW_ADD_USER_POPUP', true) } }])
    this.toolbarIconsID = toolbarIconsID
  },
  destroyed () {
    this.$store.commit('subheader/REMOVE_ICON_TOOLBAR', this.toolbarIconsID)
  }
}
</script>
